import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "redux";
import { ImportType } from "routes/Admin/Import/Import";
import { State } from "store";
import {
  ActionType,
  createReducer,
  createStandardAction,
  getType,
  StateType
} from "typesafe-actions";

type ImportActionType =
  | "UPDATE_IMPORT_DATA"
  | "LOAD_IMPORT_DATA_FROM_LOCALSTORAGE";

export const updateImportData = createStandardAction<ImportActionType>(
  "UPDATE_IMPORT_DATA"
)<ImportsState>();
export const loadImportDataFromLocalStorage = createStandardAction<
  ImportActionType
>("LOAD_IMPORT_DATA_FROM_LOCALSTORAGE")();

const ImportActions = { updateImportData, loadImportDataFromLocalStorage };
export type ImportAction = ActionType<typeof ImportActions>;

export type FileInfo = {
  name: string;
  type: string;
};
export type ImportState = {
  inProgress: boolean;
  progress: number;
  finished: boolean;
  type: ImportType;
  files: FileInfo[];
  updateExistingDevices?: boolean;
  overwriteDeviceRecords?: boolean;
};

type ImportsState = {
  [tenantId: string]: ImportState;
};

export const defaultImportState: ImportsState = {};

const IMPORTS_DATA_KEY = "imports";
const getImportDataFromLocalStorage = (): ImportsState | null => {
  try {
    const localStorageState = localStorage.getItem(IMPORTS_DATA_KEY);
    if (!localStorageState) {
      return null;
    }

    return JSON.parse(localStorageState);
  } catch (err) {
    return null;
  }
};
const saveImportDataToLocalStorage = (state: ImportsState) => {
  if (!state) {
    state = defaultImportState;
  }

  try {
    const serialized = JSON.stringify(state);
    localStorage.setItem(IMPORTS_DATA_KEY, serialized);
  } catch {
    // intentionally empty
  }
};

export const useImportActions = () => {
  const dispatch = useDispatch();
  const importData: State["import"] = useSelector(
    (state: State) => state.import
  );

  const startImport = (payload: {
    tenantId: string;
    files: FileInfo[];
    type: ImportType;
    updateExistingDevices?: boolean;
    overwriteDeviceRecords?: boolean;
  }) => {
    const newImportData = {
      ...importData,
      [payload.tenantId]: {
        ...importData[payload.tenantId],
        inProgress: true,
        progress: 0.0,
        finished: false,
        files: payload.files,
        type: payload.type,
        updateExistingDevices: payload.updateExistingDevices,
        overwriteDeviceRecords: payload.overwriteDeviceRecords
      }
    };
    dispatch(updateImportData(newImportData));
  };

  const updateProgress = (payload: { tenantId: string; progress: number }) => {
    const progress = Math.min(payload.progress, 1.0);
    const finished = progress === 1.0;
    const newImportData = {
      ...importData,
      [payload.tenantId]: {
        ...importData[payload.tenantId],
        progress,
        inProgress: !finished,
        finished
      }
    };
    dispatch(updateImportData(newImportData));
  };

  const resetImport = (payload: { tenantId: string }) => {
    const newImportData = {
      ...importData,
      [payload.tenantId]: {
        ...importData[payload.tenantId],
        inProgress: false,
        progress: 0.0,
        finished: false,
        files: [],
        updateExistingDevices: undefined,
        overwriteDeviceRecords: undefined
      }
    };

    dispatch(updateImportData(newImportData));
  };

  return [startImport, updateProgress, resetImport] as const;
};

export const ImportReducer = createReducer(defaultImportState)
  .handleAction(loadImportDataFromLocalStorage, state => {
    const importsState = getImportDataFromLocalStorage();

    return {
      ...state,
      ...(importsState ?? {})
    };
  })
  .handleAction(
    updateImportData,
    (state: ImportsState, action: { payload: ImportsState }) => ({
      ...(action.payload ? action.payload : state)
    })
  );

export const importDataMiddleware = () => (next: Dispatch) => (action: {
  type: string;
  payload: ImportsState;
}) => {
  if (getType(updateImportData) === action.type) {
    saveImportDataToLocalStorage(action.payload);
  }

  next(action);
};

export type ImportReducerState = StateType<typeof ImportReducer>;
